import type { IBreadcrumbSection, IContent, IFeatureFamily, ISection } from '../../../../types';
import { checkIfSectionIsSubmodel } from '../../../services/CategoryHelper';

export const findBreadcrumbPath = (
  sections: ISection[],
  targetId: string,
  path: ISection[] = []
): ISection[] | null => {
  for (const section of sections) {
    const nextPath = [...path, section];
    if (section.code === targetId) {
      return nextPath;
    }
    if (section.sections?.length) {
      const found = findBreadcrumbPath(section.sections, targetId, nextPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export const getBreadcrumbSection = (
  sections: ISection[],
  selectedSectionId: string
): IBreadcrumbSection | undefined => {
  const breadcrumbPath = findBreadcrumbPath(sections, selectedSectionId);
  if (!breadcrumbPath?.length) {
    return undefined;
  }
  const pathWithActive = breadcrumbPath.map((section, idx) => ({
    ...section,
    active: idx === breadcrumbPath.length - 1,
  }));
  const lastItem = pathWithActive.at(-1);
  return lastItem ? { ...lastItem, breadcrumb: pathWithActive } : undefined;
};

export const deleteSectionRecursive = (sections: ISection[], idToDelete: string): ISection[] =>
  sections
    .map((section) => ({
      ...section,
      sections: section.sections ? deleteSectionRecursive(section.sections, idToDelete) : [],
    }))
    .filter((section) => section.code !== idToDelete);

export const findFirstAvailableSectionId = (sections: ISection[]): string | null => {
  const firstSection = sections[0];
  return firstSection?.code ?? null;
};

export const isItemAssignedToSection = (sections: ISection[], itemCode: string): boolean => {
  for (const section of sections) {
    if (section.contents?.some((item) => item.code === itemCode)) {
      return true;
    }
    if (section.sections?.length && isItemAssignedToSection(section.sections, itemCode)) {
      return true;
    }
  }
  return false;
};

export const collectAssignedCodes = (sections: ISection[]): Set<string> => {
  const assignedCodes = new Set<string>();
  const processSection = (section: ISection): void => {
    for (const content of section.contents || []) {
      assignedCodes.add(content.code);
    }
    for (const subSection of section.sections || []) {
      processSection(subSection);
    }
  };
  for (const section of sections) {
    processSection(section);
  }
  return assignedCodes;
};

export const getActiveUnassignedItems = (
  featureFamily: IFeatureFamily[],
  assignedCodes: Set<string>
): IFeatureFamily[] =>
  featureFamily.filter((feature) => !assignedCodes.has(feature.code) && feature.isActive !== false);

export const removeInactiveFeatures = (
  sections: ISection[],
  isActiveContent: (content: IContent) => boolean
): ISection[] =>
  sections.map((section) => {
    if (checkIfSectionIsSubmodel(section)) {
      return section;
    }
    return {
      ...section,
      contents: section.contents?.filter(isActiveContent) ?? [],
      sections: section.sections ? removeInactiveFeatures(section.sections, isActiveContent) : [],
    };
  });
